import { Checkbox, Divider, Stack, Typography } from '@mui/material';
import ChipLabel from '/@/components/chip-label';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import DefensiveProfile, { getDefensiveProfile } from './defensive-profile';
import type { FieldRisk } from './use-field-risks';

/**
 * Hybrids of this brand are Preceon and get the branded header bar. Matches the codename
 * registered in the backend's BRAND_CODENAME_CHOICES.
 */
export const PRECEON_BRAND_CODENAME = 'PC';
/** Preceon's brand bar: dark maroon into the primary crimson, left to right. */
export const PRECEON_GRADIENT = 'linear-gradient(90deg, #2E0A14 0%, #DE0043 100%)';

export const CYCLE_LABELS: Record<string, () => string> = {
    short: () => _('Ciclo corto'),
    intermediate: () => _('Ciclo intermedio'),
    long: () => _('Ciclo largo'),
};

/**
 * Silo chip copy, matching the catalogue's wording in `product-exploration`. `not_apt` has no
 * chip: only a silo-worthy hybrid says so on the card.
 */
export const SILO_LABELS: Record<string, () => string> = {
    apt: () => _('Apto silo'),
    exclusive: () => _('Exclusivo silo'),
};

export type DensityTableValue = {
    value: number;
    minEnvironment?: number;
    maxEnvironment?: number;
    order?: number;
};

export type Hybrid = {
    id: number;
    catalogName: string;
    ranking?: number;
    cycle?: string | null;
    seedsPerBag?: number;
    metrics?: { id: number; value: number }[];
    /** Present only on ranked hybrids — the catalogue tab has no estimated yield. */
    recommended?: { yield?: number };
    brand: { id?: number; name?: string; codename?: string; metricsMaxValue?: number; type?: string };
    siloDestination?: 'not_apt' | 'apt' | 'exclusive';
    /**
     * Recommended density per environment range — a data-backed table some products carry
     * instead of (or alongside) the yield/density curve.
     */
    densityTableValues?: DensityTableValue[];
};

function RankBadge({ rank }: { rank: number }) {
    return (
        <Stack
            alignItems='center'
            justifyContent='center'
            sx={{
                minWidth: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: (theme) => theme.palette.primary.onSurface,
                color: (theme) => theme.palette.primary.contrastText,
            }}
        >
            <Typography variant='body2' fontWeight={700} color='inherit'>
                {rank}
            </Typography>
        </Stack>
    );
}

/**
 * One selectable hybrid.
 *
 * `showRank` / `showYield` are off for the catalogue tab, which the API ranks neither by
 * position nor by estimated yield.
 */
function HybridCard({
    hybrid,
    risks,
    selected,
    onToggle,
    disabled,
    compact,
    showRank = true,
    showYield = true,
}: {
    hybrid: Hybrid;
    risks: FieldRisk[];
    selected: boolean;
    onToggle: () => void;
    disabled?: boolean;
    compact?: boolean;
    showRank?: boolean;
    showYield?: boolean;
}) {
    const units = useUnitProvider();
    const isPreceon = hybrid.brand?.codename === PRECEON_BRAND_CODENAME;
    const cycleLabel = hybrid.cycle ? CYCLE_LABELS[hybrid.cycle]?.() : undefined;
    const siloLabel = hybrid.siloDestination ? SILO_LABELS[hybrid.siloDestination]?.() : undefined;
    const rows = getDefensiveProfile(risks, hybrid.metrics, hybrid.brand?.metricsMaxValue ?? 9);

    const estimatedYield = hybrid.recommended?.yield;
    const yieldValue =
        showYield && estimatedYield ? units.toYield(estimatedYield, hybrid.brand?.type || 'corn', 0) : undefined;
    const hasYield = !!yieldValue && yieldValue.localeValue !== 'NaN';

    // The chips sit on their own row: inline beside a long hybrid name they get clipped.
    const chips = (cycleLabel || siloLabel) && (
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {cycleLabel && <ChipLabel label={cycleLabel} type='cycle' size='sm' />}
            {siloLabel && <ChipLabel label={siloLabel} type='rv' size='sm' />}
        </Stack>
    );

    const header = (
        <Stack direction='row' spacing={2} alignItems='center'>
            {showRank && hybrid.ranking !== undefined && <RankBadge rank={hybrid.ranking} />}
            <Stack spacing={0.5}>
                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                    {hybrid.catalogName}
                </Typography>
                {chips}
            </Stack>
        </Stack>
    );

    return (
        <Stack
            onClick={disabled ? undefined : onToggle}
            data-testid={`hybrid-card-${hybrid.id}`}
            sx={{
                position: 'relative',
                height: '100%',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: (theme) => (selected ? theme.palette.primary.onSurface : theme.palette.divider),
                backgroundColor: (theme) => (selected ? theme.palette.action.hover : theme.palette.background.paper),
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: '-1px',
                    borderRadius: 'inherit',
                    border: '2px solid',
                    borderColor: (theme) => (selected ? theme.palette.primary.onSurface : 'transparent'),
                    pointerEvents: 'none',
                },
            }}
        >
            {isPreceon && (
                <Stack px={2} py={0.75} sx={{ background: PRECEON_GRADIENT }}>
                    <Typography variant='subtitle2' fontWeight='bold' color='common.white' textTransform='uppercase'>
                        {_('Preceon')}
                    </Typography>
                </Stack>
            )}

            {compact ? (
                <Stack p={2} spacing={1}>
                    <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={1}>
                        {header}
                        <Checkbox checked={selected} disabled={disabled} sx={{ p: 0 }} />
                    </Stack>

                    {hasYield && (
                        <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
                            <Stack direction='row'>
                                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                    {yieldValue.localeValue} {yieldValue.unit}
                                </Typography>
                            </Stack>
                            <Typography variant='subtitle2' color='primary.mui.inactive'>
                                {_('Rendimiento estimado')}
                            </Typography>
                        </Stack>
                    )}
                    <DefensiveProfile rows={rows} compact />
                </Stack>
            ) : (
                <Stack height='100%'>
                    <Stack p={2} spacing={2} flexGrow={1}>
                        <Stack spacing={1}>{header}</Stack>
                        {hasYield && (
                            <Stack>
                                <Typography variant='h4' color='primary'>
                                    {yieldValue.localeValue}{' '}
                                    <Typography variant='h6' component='span' color='primary'>
                                        {yieldValue.unit}
                                    </Typography>
                                </Typography>
                                <Typography variant='caption' color='primary.mui.inactive'>
                                    {_('Rendimiento estimado')}
                                </Typography>
                            </Stack>
                        )}
                        <DefensiveProfile rows={rows} />
                    </Stack>
                    <Divider />
                    <Stack direction='row' alignItems='center' justifyContent='flex-end' p={2} spacing={1}>
                        <Typography variant='body2' fontWeight={700} color='primary'>
                            {_('Seleccionar')}
                        </Typography>
                        <Checkbox checked={selected} disabled={disabled} sx={{ p: 0 }} color='info' size='large' />
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default HybridCard;
